import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Hero from '../../components/Hero';
import { Link } from 'react-router-dom';
import CallToAction from '../../components/CallToAction';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons';
// EmailJS removed - using AWS Lambda instead
import '../Projects.css';

// Import images
import projectsHeroImage from '../../assets/images/projects.jpg';
import edtechImage from '../../assets/images/edtech.jpg';
import aiImage from '../../assets/images/ai.jpg';
import softwareImage from '../../assets/images/software.jpg';
import webImage from '../../assets/images/web.jpg';
import kurumImage from '../../assets/images/kurum.jpg';
import socialImage from '../../assets/images/social.jpg';

const ProjectsEN = () => {
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [projectFormData, setProjectFormData] = useState({
    name: '',
    email: '',
    phone: '',
    customerType: 'individual',
    projectType: '',
    projectDetails: '',
    message: ''
  });
  const [isSubmittingProject, setIsSubmittingProject] = useState(false);
  const [projectSubmitStatus, setProjectSubmitStatus] = useState(null);

  const projectTypes = [
    'Education Technologies',
    'Data Science and Artificial Intelligence',
    'Mobile and Web Applications',
    'Corporate Web Solutions',
    'Corporate Software',
    'Other'
  ];

  const handleProjectFormChange = (e) => {
    const { name, value } = e.target;
    setProjectFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    if (projectSubmitStatus) setProjectSubmitStatus(null);
  };

  const handleProjectFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingProject(true);
    setProjectSubmitStatus(null);

    const lambdaUrl = process.env.REACT_APP_LAMBDA_PROJECT_REQUEST_URL;

    if (!lambdaUrl) {
      console.error('Lambda URL configuration missing!');
      setIsSubmittingProject(false);
      setProjectSubmitStatus('error');
      return;
    }

    try {
      const response = await fetch(lambdaUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: projectFormData.name,
          email: projectFormData.email,
          phone: projectFormData.phone || '',
          customerType: projectFormData.customerType,
          projectType: projectFormData.projectType,
          projectDetails: projectFormData.projectDetails || '',
          message: projectFormData.message || '',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'An error occurred while sending the project request.');
      }

      setProjectSubmitStatus('success');
      setProjectFormData({
        name: '',
        email: '',
        phone: '',
        customerType: 'individual',
        projectType: '',
        projectDetails: '',
        message: ''
      });
      setTimeout(() => setProjectSubmitStatus(null), 3000);
    } catch (error) {
      console.error('Lambda Error:', error);
      setProjectSubmitStatus('error');
      setTimeout(() => setProjectSubmitStatus(null), 3000);
    } finally {
      setIsSubmittingProject(false);
    }
  };

  const projectsData = [
    {
      id: 1,
      title: 'Education Technologies',
      description: 'ProgenX Academy platform is an innovative solution that offers AI-powered personalized learning experiences and creates optimized educational programs according to students\' individual learning needs.',
      category: 'edtech',
      image: edtechImage,
      path: '/en/projects/education-technologies'
    },
    {
      id: 2,
      title: 'Data Science and Artificial Intelligence',
      description: 'We develop advanced data analysis and artificial intelligence solutions that extract meaningful information from large data sets and empower businesses\' decision-making processes.',
      category: 'ai',
      image: aiImage,
      path: '/en/projects/data-science-ai'
    },
    {
      id: 3,
      title: 'Mobile and Web Applications',
      description: 'We create user-friendly, high-performance digital products and develop mobile and web applications that digitize business processes.',
      category: 'software',
      image: softwareImage,
      path: '/en/projects/mobile-web-applications'
    },
    {
      id: 4,
      title: 'Corporate Web Solutions',
      description: 'We offer integrated digital solutions for businesses that strengthen our customers\' digital presence by creating modern and impressive websites.',
      category: 'web',
      image: webImage,
      path: '/en/projects/corporate-web-solutions'
    },
    {
      id: 5,
      title: 'Software Support for Institutions and Publishers',
      description: 'We offer interactive content, SCORM-compliant educational materials, smart board applications, and corporate systems specially developed for educational institutions and publishers.',
      category: 'corporate',
      image: kurumImage,
      path: '/en/projects/corporate-software'
    },
    {
      id: 6,
      title: 'Social Responsibility Projects',
      description: 'Within the scope of HAVELSAN F5 Executive Development Academy, we offered students at Hatay Naim Atakaş Anatolian High School the opportunity to use the beta version of our software product in education technologies free of charge.',
      category: 'social',
      image: socialImage,
      path: '/en/projects/social-responsibility'
    }
  ];

  const faqData = [
    {
      question: "What areas does ProgenX develop projects in?",
      answer: "ProgenX works in education technologies, data science and artificial intelligence, mobile and web applications, corporate web solutions, software support for educational institutions and publishers, and social responsibility projects."
    },
    {
      question: "What do your education technology projects offer?",
      answer: "We offer AI-powered personalized learning platforms, smart question-solving systems, detailed progress tracking, and fun interactive content. We enable each student to learn at their own pace."
    },
    {
      question: "What are your data science and artificial intelligence solutions used for?",
      answer: "We analyze businesses' data to extract meaningful insights. We make future predictions, make automated decisions, and optimize processes. This way, businesses make better decisions and reduce their costs."
    },
    {
      question: "How does the mobile and web application development process work?",
      answer: "We develop user-friendly and fast applications by understanding your business needs. We offer reliable and modern solutions that provide access to your customers from anywhere. We work closely with you during the project and provide support after development."
    },
    {
      question: "What kind of solutions do you offer for educational institutions?",
      answer: "We develop standards-compliant educational content, interactive learning materials, smart board applications, and corporate management systems for educational institutions. We make educational processes more efficient."
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "ProgenX",
    "url": "https://progenx.com.tr",
    "logo": "https://progenx.com.tr/logo.png",
    "description": "ProgenX is a technology company offering innovative solutions in education technologies, data science, artificial intelligence, and mobile and web application development.",
    "sameAs": [
      "https://www.linkedin.com/company/progenx",
      "https://twitter.com/progenx"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "ProgenX Projects",
      "itemListElement": projectsData.map((project, index) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": project.title,
          "description": project.description,
          "url": `https://progenx.com.tr${project.path}`
        },
        "position": index + 1
      }))
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="projects">
      <Helmet>
        <title>ProgenX Projects | Education Technologies, AI and Software Solutions 2026</title>
        <meta name="description" content="ProgenX's education technologies, AI-powered learning platforms, data science solutions, mobile application development, corporate websites, and social responsibility projects. Turkey's leading technology solutions company." />
        <meta name="keywords" content="progenx projects, education technology, ai solutions, data science, mobile app development, web app development, corporate software, education software, digital transformation, turkey technology company" />
        <meta property="og:title" content="ProgenX Projects | Education Technologies and Software Solutions" />
        <meta property="og:description" content="Innovative solutions in education technologies, artificial intelligence, data science, mobile and web application development. Start your digital transformation with ProgenX." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://progenx.com.tr/en/projects" />
        <meta property="og:image" content="https://progenx.com.tr/projects.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ProgenX Projects | Technology Solutions" />
        <meta name="twitter:description" content="Start your digital transformation with education technologies, artificial intelligence, and software solutions." />
        <link rel="canonical" href="https://progenx.com.tr/en/projects" />
        <link rel="alternate" hreflang="tr" href="https://progenx.com.tr/projeler" />
        <link rel="alternate" hreflang="en" href="https://progenx.com.tr/en/projects" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>
      
      <Hero 
        title="PROJECTS" 
        backgroundImage={projectsHeroImage}
      />
      
      <section className="projects-content">
        <div className="container">
          <h2 className="section-title">What We Do</h2>
          <p className="projects-intro">
            As ProgenX, we develop digital solutions across a wide spectrum from education to business. 
            From AI-powered education platforms to analytical solutions that transform businesses' data into value, 
            from modern web and mobile applications to corporate software solutions, we use technology to 
            make people's lives easier.
          </p>
          
          <div className="projects-grid">
            {projectsData.map(project => (
              <Link to={project.path} key={project.id} className="project-item">
                <img 
                  src={project.image} 
                  alt={`ProgenX ${project.title} project - ${project.description.substring(0, 50)}...`}
                  className="project-image"
                  loading="lazy"
                />
                <h3 className="project-title">{project.title}</h3>
                <p className="project-description">{project.description}</p>
                <div className="project-category">{project.category}</div>
              </Link>
            ))}
          </div>
          
          <div className="support-section">
            <h3>Work With Us For Your Project</h3>
            <p>
              We are with you on your digital transformation journey. From educational institutions to businesses, 
              from individual projects to corporate solutions, we offer professional support for all your needs. 
              Let's turn your dream project into reality together.
            </p>
            <div className="support-buttons">
              <button 
                onClick={() => setShowProjectForm(true)} 
                className="btn btn-primary"
              >
                SEND PROJECT REQUEST
              </button>
              <Link to="/en/contact" className="btn">CONTACT US</Link>
            </div>
          </div>

          {/* Project Request Form */}
          <div className="project-request-section">
            <h2 className="section-title">Send Project Request</h2>
            <p className="section-subtitle">
              Submit your project request as an institution or individual. We will get back to you as soon as possible.
            </p>
            <div className="project-request-form-container">
              {projectSubmitStatus === 'success' && (
                <div className="form-message success">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>Sent</span>
                </div>
              )}
              {projectSubmitStatus === 'error' && (
                <div className="form-message error">
                  <span>Failed</span>
                </div>
              )}
              <form onSubmit={handleProjectFormSubmit} className="project-request-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input 
                      type="text" 
                      name="name" 
                      value={projectFormData.name} 
                      onChange={handleProjectFormChange} 
                      placeholder="Full Name"
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label>Email Address *</label>
                    <input 
                      type="email" 
                      name="email" 
                      value={projectFormData.email} 
                      onChange={handleProjectFormChange} 
                      placeholder="example@email.com"
                      required 
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Phone (Optional)</label>
                    <input 
                      type="tel" 
                      name="phone" 
                      value={projectFormData.phone} 
                      onChange={handleProjectFormChange} 
                      placeholder="+90 555 123 45 67"
                    />
                  </div>
                  <div className="form-group">
                    <label>Customer Type *</label>
                    <select 
                      name="customerType" 
                      value={projectFormData.customerType} 
                      onChange={handleProjectFormChange} 
                      required
                    >
                      <option value="individual">Individual</option>
                      <option value="company">Company</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Project Type *</label>
                  <select 
                    name="projectType" 
                    value={projectFormData.projectType} 
                    onChange={handleProjectFormChange} 
                    required
                  >
                    <option value="">Select...</option>
                    {projectTypes.map((type, index) => (
                      <option key={index} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                {projectFormData.projectType === 'Other' && (
                  <div className="form-group">
                    <label>Project Details *</label>
                    <textarea 
                      name="projectDetails" 
                      value={projectFormData.projectDetails} 
                      onChange={handleProjectFormChange} 
                      placeholder="Please describe your project in detail..."
                      rows="4"
                      required
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>Message (Optional)</label>
                  <textarea 
                    name="message" 
                    value={projectFormData.message} 
                    onChange={handleProjectFormChange} 
                    placeholder="Is there anything else you would like to add?"
                    rows="4"
                  />
                </div>
                <button 
                  type="submit" 
                  className="btn btn-primary" 
                  disabled={isSubmittingProject}
                  style={{ width: '100%', maxWidth: '400px', margin: '20px auto 0', display: 'block' }}
                >
                  {isSubmittingProject ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin /> Sending...
                    </>
                  ) : (
                    'SEND PROJECT REQUEST'
                  )}
                </button>
              </form>
            </div>
          </div>

          {/* Project Request Modal */}
          {showProjectForm && (
            <div className="project-form-modal" onClick={() => setShowProjectForm(false)}>
              <div className="project-form-content" onClick={(e) => e.stopPropagation()}>
                <button 
                  className="close-modal-btn"
                  onClick={() => setShowProjectForm(false)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
                <h3>Send Project Request</h3>
                {projectSubmitStatus === 'success' && (
                  <div className="form-message success">
                    <FontAwesomeIcon icon={faCheckCircle} />
                    <span>Your project request has been sent successfully! We will get back to you as soon as possible.</span>
                  </div>
                )}
                {projectSubmitStatus === 'error' && (
                  <div className="form-message error">
                    <span>An error occurred. Please try again.</span>
                  </div>
                )}
                <form onSubmit={handleProjectFormSubmit}>
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input 
                      type="text" 
                      name="name" 
                      value={projectFormData.name} 
                      onChange={handleProjectFormChange} 
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label>Email Address *</label>
                    <input 
                      type="email" 
                      name="email" 
                      value={projectFormData.email} 
                      onChange={handleProjectFormChange} 
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone (Optional)</label>
                    <input 
                      type="tel" 
                      name="phone" 
                      value={projectFormData.phone} 
                      onChange={handleProjectFormChange} 
                    />
                  </div>
                  <div className="form-group">
                    <label>Customer Type *</label>
                    <select 
                      name="customerType" 
                      value={projectFormData.customerType} 
                      onChange={handleProjectFormChange} 
                      required
                    >
                      <option value="individual">Individual</option>
                      <option value="company">Company</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Project Type *</label>
                    <select 
                      name="projectType" 
                      value={projectFormData.projectType} 
                      onChange={handleProjectFormChange} 
                      required
                    >
                      <option value="">Select...</option>
                      {projectTypes.map((type, index) => (
                        <option key={index} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  {projectFormData.projectType === 'Other' && (
                    <div className="form-group">
                      <label>Project Details *</label>
                      <textarea 
                        name="projectDetails" 
                        value={projectFormData.projectDetails} 
                        onChange={handleProjectFormChange} 
                        placeholder="Please describe your project in detail..."
                        rows="4"
                        required
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label>Message (Optional)</label>
                    <textarea 
                      name="message" 
                      value={projectFormData.message} 
                      onChange={handleProjectFormChange} 
                      placeholder="Is there anything else you would like to add?"
                      rows="4"
                    />
                  </div>
                  <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={isSubmittingProject}
                  >
                    {isSubmittingProject ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin /> Sending...
                      </>
                    ) : (
                      'SEND'
                    )}
                  </button>
                </form>
              </div>
            </div>
          )}

          <section className="faq-section">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <div className="faq-container">
              {faqData.map((faq, index) => (
                <div key={index} className="faq-item">
                  <h3 className="faq-question">{faq.question}</h3>
                  <p className="faq-answer">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </section>
      
      <CallToAction 
        title="Let's Shape Your Digital Future Together"
        subtitle="We are here to turn your dreams into reality with technology."
        buttonText="Contact Us" 
        buttonLink="/en/contact" 
      />
    </div>
  );
};

export default ProjectsEN;
